import { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import { CheckCircle2, X, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useExperiment } from "@/hooks/useExperiment";

const TOAST_VARIANTS = ["finish", "lock", "free"] as const;
type ToastVariant = (typeof TOAST_VARIANTS)[number];

const TOAST_COPY: Record<
  ToastVariant,
  { headline: (n?: string) => string; body: (hasEmail: boolean) => string; cta: string }
> = {
  finish: {
    headline: (n) => (n ? `${n}, we saved your progress.` : "We saved your progress."),
    body: (hasEmail) =>
      hasEmail
        ? "Check your email for a link to finish your quote — or tap below to pick up where you left off."
        : "Tap below to finish your free fence quote — takes about 30 seconds.",
    cta: "Finish quote",
  },
  lock: {
    headline: (n) => (n ? `${n}, lock in today's pricing.` : "Lock in today's pricing."),
    body: (hasEmail) =>
      hasEmail
        ? "We saved your details — finish in 30 seconds to lock today's pricing before it changes."
        : "We saved your spot. Finish in 30 seconds to lock today's pricing.",
    cta: "Lock my price",
  },
  free: {
    headline: (n) => (n ? `${n}, get your free quote.` : "Get your free quote."),
    body: () =>
      "You're 30 seconds away from a free, no-obligation quote. We'll never share your info.",
    cta: "Get my free quote",
  },
};

interface DraftPayload {
  name?: string;
  phone?: string;
  email?: string;
  page_path?: string;
  source?: string;
  last_field?: string;
}

/**
 * Floating "we saved your progress" prompt.
 * Appears when useFormDraftCapture fires the `draft-followup-fired` event
 * (exit-intent or 2-min idle timeout). Gives the user a one-tap way back
 * to the form they started, plus a tap-to-call fallback.
 */
const DraftFollowupToast = () => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftPayload>({});

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { draft?: DraftPayload } | undefined;
      if (!detail?.draft) return;
      setDraft(detail.draft);
      setOpen(true);
    };
    window.addEventListener("draft-followup-fired", handler);
    return () => window.removeEventListener("draft-followup-fired", handler);
  }, []);

  if (!open) return null;
  return <ToastInner draft={draft} onClose={() => setOpen(false)} />;
};

const ToastInner = ({ draft, onClose }: { draft: DraftPayload; onClose: () => void }) => {
  // Only assigned + counted as a "view" when the toast actually renders.
  const { variant, track } = useExperiment(
    "draft_followup_toast_copy",
    TOAST_VARIANTS as unknown as string[],
  );
  const copy = TOAST_COPY[(variant as ToastVariant) ?? "finish"] ?? TOAST_COPY.finish;

  const firstName = draft.name?.trim().split(/\s+/)[0];
  const basePath = draft.page_path && draft.page_path !== "/" ? draft.page_path : "/contact";
  const params = new URLSearchParams({ resume: "1" });
  if (draft.source) params.set("src", draft.source);
  if (draft.last_field) params.set("field", draft.last_field);
  const resumePath = `${basePath}?${params.toString()}`;

  const handleResumeClick = () => {
    track("cta_click", { placement: "draft_followup_toast", source: draft.source });
    onClose();
    void import("@/lib/analytics").then(({ trackEvent }) =>
      trackEvent("draft_followup_resume_click", {
        source: draft.source,
        last_field: draft.last_field,
        page_path: draft.page_path,
        variant,
      }),
    );
  };

  const handleCallClick = () => {
    track("call_click", { placement: "draft_followup_toast" });
    onClose();
  };

  const handleDismiss = () => {
    track("dismiss");
    onClose();
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 z-[60] max-w-sm sm:w-96 rounded-xl border border-border bg-background shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-300"
      role="dialog"
      aria-live="polite"
      aria-label="We saved your fence quote"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm text-foreground leading-snug">
              {copy.headline(firstName)}
            </p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {copy.body(Boolean(draft.email))}
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button asChild size="sm" className="h-10 text-xs gap-1">
            <Link to={resumePath} onClick={handleResumeClick}>
              {copy.cta} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="h-10 text-xs gap-1">
            <a href="tel:+18884163144" onClick={handleCallClick}>
              <Phone className="w-3.5 h-3.5" /> Call us
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DraftFollowupToast;
